import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { BookOpen, Loader2, Plus, Pencil, Trash2, Eye, EyeOff, X } from "lucide-react";
import { toast } from "sonner";

type Outcome = "succeeded" | "partial" | "paused" | "failed" | "ongoing";
type Visibility = "private" | "public";

type Lesson = {
  id: string;
  project_id: string;
  user_id: string;
  outcome: Outcome;
  summary: string;
  what_worked: string | null;
  what_didnt_work: string | null;
  what_id_do_differently: string | null;
  advice_for_others: string | null;
  tags: string[];
  visibility: Visibility;
  created_at: string;
  updated_at: string;
};

const OUTCOMES: { value: Outcome; label: string; cls: string }[] = [
  { value: "succeeded", label: "Succeeded", cls: "bg-primary/15 text-primary" },
  { value: "partial", label: "Partial win", cls: "bg-amber-500/15 text-amber-500" },
  { value: "paused", label: "Paused", cls: "bg-muted text-muted-foreground" },
  { value: "failed", label: "Failed", cls: "bg-destructive/15 text-destructive" },
  { value: "ongoing", label: "Ongoing", cls: "bg-secondary text-secondary-foreground" },
];

const outcomeMeta = (o: Outcome) => OUTCOMES.find((x) => x.value === o) ?? OUTCOMES[4];

type FormState = {
  outcome: Outcome;
  summary: string;
  what_worked: string;
  what_didnt_work: string;
  what_id_do_differently: string;
  advice_for_others: string;
  tagsInput: string;
  visibility: Visibility;
};

const emptyForm: FormState = {
  outcome: "ongoing",
  summary: "",
  what_worked: "",
  what_didnt_work: "",
  what_id_do_differently: "",
  advice_for_others: "",
  tagsInput: "",
  visibility: "public",
};

const lessonToForm = (l: Lesson): FormState => ({
  outcome: l.outcome,
  summary: l.summary,
  what_worked: l.what_worked ?? "",
  what_didnt_work: l.what_didnt_work ?? "",
  what_id_do_differently: l.what_id_do_differently ?? "",
  advice_for_others: l.advice_for_others ?? "",
  tagsInput: (l.tags ?? []).join(", "),
  visibility: l.visibility,
});

interface Props {
  projectId: string;
  isOwner: boolean;
  ownerUserId: string;
}

const LessonsLearnedPanel = ({ projectId, isOwner, ownerUserId }: Props) => {
  const qc = useQueryClient();
  const [composerOpen, setComposerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);

  const { data: lessons, isLoading } = useQuery({
    queryKey: ["project-lessons", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_lessons")
        .select("*")
        .eq("project_id", projectId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Lesson[];
    },
    enabled: !!projectId,
  });

  const resetComposer = () => {
    setForm(emptyForm);
    setEditingId(null);
    setComposerOpen(false);
  };

  const save = useMutation({
    mutationFn: async () => {
      const tags = form.tagsInput
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean)
        .slice(0, 12);

      if (!form.summary.trim()) {
        throw new Error("Add a short summary so this lesson is searchable.");
      }

      const payload = {
        project_id: projectId,
        user_id: ownerUserId,
        outcome: form.outcome,
        summary: form.summary.trim(),
        what_worked: form.what_worked.trim() || null,
        what_didnt_work: form.what_didnt_work.trim() || null,
        what_id_do_differently: form.what_id_do_differently.trim() || null,
        advice_for_others: form.advice_for_others.trim() || null,
        tags,
        visibility: form.visibility,
      };

      if (editingId) {
        const { error } = await supabase
          .from("project_lessons")
          .update(payload)
          .eq("id", editingId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("project_lessons").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success(editingId ? "Lesson updated." : "Lesson captured.");
      qc.invalidateQueries({ queryKey: ["project-lessons", projectId] });
      resetComposer();
    },
    onError: (e: any) => toast.error(e?.message ?? "Save failed"),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("project_lessons").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Lesson deleted.");
      qc.invalidateQueries({ queryKey: ["project-lessons", projectId] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Delete failed"),
  });

  const startEdit = (l: Lesson) => {
    setEditingId(l.id);
    setForm(lessonToForm(l));
    setComposerOpen(true);
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-primary" />
          <h3 className="font-display text-lg font-semibold">Lessons Learned</h3>
        </div>
        {isOwner && !composerOpen && (
          <Button size="sm" variant="outline" onClick={() => setComposerOpen(true)}>
            <Plus className="h-4 w-4 mr-1" /> Capture lesson
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Success and failure are equally valuable. Capture outcomes, what worked, what didn't, and what
        others should know — public lessons feed the Alabama Archive.
      </p>

      {composerOpen && isOwner && (
        <div className="border border-border rounded-lg p-4 mb-4 space-y-3 bg-muted/10">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Outcome</Label>
              <Select
                value={form.outcome}
                onValueChange={(v) => setForm((f) => ({ ...f, outcome: v as Outcome }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {OUTCOMES.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Visibility</Label>
              <Select
                value={form.visibility}
                onValueChange={(v) => setForm((f) => ({ ...f, visibility: v as Visibility }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">Public — share with the Archive</SelectItem>
                  <SelectItem value="private">Private — only I can see this</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Summary *</Label>
            <Input
              placeholder="One line that captures the takeaway"
              value={form.summary}
              onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
              maxLength={240}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">What worked</Label>
              <Textarea
                rows={3}
                value={form.what_worked}
                onChange={(e) => setForm((f) => ({ ...f, what_worked: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">What didn't work</Label>
              <Textarea
                rows={3}
                value={form.what_didnt_work}
                onChange={(e) => setForm((f) => ({ ...f, what_didnt_work: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">What I'd do differently</Label>
              <Textarea
                rows={3}
                value={form.what_id_do_differently}
                onChange={(e) =>
                  setForm((f) => ({ ...f, what_id_do_differently: e.target.value }))
                }
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Advice for others</Label>
              <Textarea
                rows={3}
                value={form.advice_for_others}
                onChange={(e) => setForm((f) => ({ ...f, advice_for_others: e.target.value }))}
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Tags (comma-separated)</Label>
            <Input
              placeholder="funding, permitting, community-engagement"
              value={form.tagsInput}
              onChange={(e) => setForm((f) => ({ ...f, tagsInput: e.target.value }))}
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={resetComposer} disabled={save.isPending}>
              <X className="h-4 w-4 mr-1" /> Cancel
            </Button>
            <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-1" />
              )}
              {editingId ? "Save changes" : "Save lesson"}
            </Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : !lessons || lessons.length === 0 ? (
        <div className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-6 text-center">
          No lessons captured yet.
          {isOwner ? " Close the loop on this project so others can learn from it." : ""}
        </div>
      ) : (
        <div className="space-y-3">
          {lessons.map((l) => {
            const meta = outcomeMeta(l.outcome);
            return (
              <div key={l.id} className="border border-border rounded-lg p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${meta.cls}`}
                    >
                      {meta.label}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                      {l.visibility === "public" ? (
                        <>
                          <Eye className="h-3 w-3" /> Public
                        </>
                      ) : (
                        <>
                          <EyeOff className="h-3 w-3" /> Private
                        </>
                      )}
                    </span>
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      {new Date(l.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  {isOwner && (
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="ghost" onClick={() => startEdit(l)} aria-label="Edit">
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => {
                          if (confirm("Delete this lesson?")) remove.mutate(l.id);
                        }}
                        aria-label="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
                <p className="text-sm font-semibold mb-2">{l.summary}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                  {l.what_worked && (
                    <Field label="What worked" value={l.what_worked} />
                  )}
                  {l.what_didnt_work && (
                    <Field label="What didn't work" value={l.what_didnt_work} />
                  )}
                  {l.what_id_do_differently && (
                    <Field label="What I'd do differently" value={l.what_id_do_differently} />
                  )}
                  {l.advice_for_others && (
                    <Field label="Advice for others" value={l.advice_for_others} />
                  )}
                </div>
                {l.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {l.tags.map((t) => (
                      <Badge key={t} variant="secondary" className="text-[10px]">
                        {t}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value }: { label: string; value: string }) => (
  <div>
    <div className="text-muted-foreground uppercase tracking-wide text-[10px] mb-1">{label}</div>
    <div className="text-foreground whitespace-pre-wrap">{value}</div>
  </div>
);

export default LessonsLearnedPanel;